/**
 * AxLabs Biology Experiments - Genetics
 *
 * Collection of genetics experiments generated using
 * the declarative framework.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState, getStateValues } from '../../physics/generated/helpers.js';
// ============================================================================
// HARDY-WEINBERG EQUILIBRIUM
// ============================================================================
export const HardyWeinbergEquilibrium = createExperimentFromSchema({
    id: 'hardy-weinberg-equilibrium',
    name: 'Hardy-Weinberg Equilibrium',
    domain: 'biology',
    description: 'Allele and genotype frequencies in a non-evolving population',
    conceptTags: ['Hardy-Weinberg', 'Allele Frequency', 'Genotype Frequency', 'Population Genetics'],
    difficulty: 4,
    stateVariables: [
        { id: 'p', name: 'Allele A Frequency', unit: '', initial: 0.7, symbol: 'p', description: 'Frequency of dominant allele A' },
    ],
    parameters: [
        { id: 'N', name: 'Population Size', unit: 'individuals', min: 100, max: 100000, default: 10000, symbol: 'N', description: 'Total population size' },
    ],
    equations: [
        {
            id: 'hw-equations',
            name: 'Hardy-Weinberg Equations',
            latex: 'p + q = 1, \\quad p^2 + 2pq + q^2 = 1',
            description: 'Allele and genotype frequency relationships',
        },
        {
            id: 'genotype-frequencies',
            name: 'Genotype Frequencies',
            latex: 'f(AA) = p^2, \\quad f(Aa) = 2pq, \\quad f(aa) = q^2',
            description: 'Expected genotype frequencies at equilibrium',
        },
    ],
    derivatives: (_state, _params) => ({
        p: 0,
    }),
    derivedState: (state, params) => {
        const p = getState(state, 'p');
        const N = getParam(params, 'N');
        const q = 1 - p;
        const f_AA = p * p;
        const f_Aa = 2 * p * q;
        const f_aa = q * q;
        const N_AA = Math.round(N * f_AA);
        const N_Aa = Math.round(N * f_Aa);
        const N_aa = Math.round(N * f_aa);
        return {
            q,
            f_AA,
            f_Aa,
            f_aa,
            N_AA,
            N_Aa,
            N_aa,
            heterozygosity: 2 * p * q,
        };
    },
    failureModes: [
        {
            id: 'invalid-frequency',
            name: 'Invalid Allele Frequency',
            check: (_state, _params) => {
                const p = getState(getStateValues(_state), 'p');
                return p < 0 || p > 1;
            },
            severity: 'error',
            educationalMessage: 'Allele frequency p must be between 0 and 1. Since p + q = 1, both frequencies must be in the range [0, 1].',
        },
    ],
    bounds: {
        p: { min: 0, max: 1 },
    },
});
// ============================================================================
// GENETIC DRIFT (SMALL POPULATION)
// ============================================================================
export const GeneticDrift = createExperimentFromSchema({
    id: 'genetic-drift',
    name: 'Genetic Drift (Small Population)',
    domain: 'biology',
    description: 'Random changes in allele frequencies due to finite population size',
    conceptTags: ['Genetic Drift', 'Population Bottleneck', 'Fixation Probability', 'Effective Population Size'],
    difficulty: 6,
    stateVariables: [
        { id: 'p', name: 'Allele A Frequency', unit: '', initial: 0.5, symbol: 'p' },
        { id: 't', name: 'Generation', unit: 'generations', initial: 0, symbol: 't' },
    ],
    parameters: [
        { id: 'N_e', name: 'Effective Population Size', unit: 'individuals', min: 10, max: 1000, default: 100, symbol: 'N_e', description: 'Effective breeding population size' },
    ],
    equations: [
        {
            id: 'drift-variance',
            name: 'Variance in Allele Frequency Change',
            latex: '\\sigma^2_{\\Delta p} = \\frac{p(1-p)}{2N_e}',
            description: 'Variance of change in allele frequency per generation',
        },
        {
            id: 'fixation-probability',
            name: 'Fixation Probability',
            latex: 'P_{fix} = p_0',
            description: 'Probability that allele A will eventually become fixed',
        },
        {
            id: 'time-to-fixation',
            name: 'Expected Time to Fixation',
            latex: '\\bar{t}_{fix} \\approx -4N_e[p \\ln p + (1-p)\\ln(1-p)]',
            description: 'Expected generations until fixation or loss',
        },
    ],
    derivatives: (state, params) => {
        const N_e = getParam(params, 'N_e');
        const p = getState(state, 'p');
        const variance = p * (1 - p) / (2 * N_e);
        const std = Math.sqrt(variance);
        return {
            p: 0,
            t: 1,
            drift_noise: std,
        };
    },
    derivedState: (state, params) => {
        const p = getState(state, 'p');
        const N_e = getParam(params, 'N_e');
        const q = 1 - p;
        const variance = p * q / (2 * N_e);
        const heterozygosity_H = 2 * p * q;
        const H_expected = 1 - 1 / (2 * N_e);
        // Expected time to fixation (approximation)
        let t_fix = 0;
        if (p > 0 && p < 1) {
            t_fix = -4 * N_e * (p * Math.log(p) + q * Math.log(q));
        }
        const fixation_prob = p;
        const loss_prob = q;
        return {
            q,
            variance,
            std_dev: Math.sqrt(variance),
            heterozygosity_H,
            heterozygosity_expected: H_expected,
            t_fix_approx: t_fix,
            fixation_prob,
            loss_prob,
            is_fixed: p === 1 ? 1 : 0,
            is_lost: p === 0 ? 1 : 0,
        };
    },
    failureModes: [
        {
            id: 'invalid-frequency',
            name: 'Invalid Allele Frequency',
            check: (_state, _params) => {
                const p = getState(getStateValues(_state), 'p');
                return p < 0 || p > 1;
            },
            severity: 'error',
            educationalMessage: 'Allele frequency must be between 0 and 1.',
        },
    ],
    bounds: {
        p: { min: 0, max: 1 },
        t: { min: 0, max: 10000 },
    },
});
// ============================================================================
// NATURAL SELECTION (DIRECTIONAL)
// ============================================================================
export const NaturalSelectionDirectional = createExperimentFromSchema({
    id: 'natural-selection-directional',
    name: 'Natural Selection (Directional)',
    domain: 'biology',
    description: 'Change in allele frequency under directional selection pressure',
    conceptTags: ['Natural Selection', 'Directional Selection', 'Fitness', 'Selection Coefficient'],
    difficulty: 5,
    stateVariables: [
        { id: 'p', name: 'Allele A Frequency', unit: '', initial: 0.5, symbol: 'p', description: 'Frequency of beneficial allele A' },
        { id: 't', name: 'Generation', unit: 'generations', initial: 0, symbol: 't' },
    ],
    parameters: [
        { id: 's', name: 'Selection Coefficient', unit: '', min: -0.5, max: 1, default: 0.1, symbol: 's', description: 'Selective advantage of allele A (positive = favored)' },
        { id: 'h', name: 'Dominance Coefficient', unit: '', min: 0, max: 1, default: 0.5, symbol: 'h', description: 'h=0: A recessive, h=1: A dominant, h=0.5: additive' },
    ],
    equations: [
        {
            id: 'fitness-values',
            name: 'Genotype Fitness Values',
            latex: 'w_{AA} = 1 + s, \\quad w_{Aa} = 1 + hs, \\quad w_{aa} = 1',
            description: 'Relative fitness of each genotype',
        },
        {
            id: 'mean-fitness',
            name: 'Mean Population Fitness',
            latex: '\\bar{w} = p^2 w_{AA} + 2pq w_{Aa} + q^2 w_{aa}',
            description: 'Average fitness across all genotypes',
        },
        {
            id: 'allele-change',
            name: 'Allele Frequency Change',
            latex: '\\Delta p = \\frac{pq[\\bar{w}_A - \\bar{w}_a]}{\\bar{w}}',
            description: 'Change in allele frequency per generation',
        },
    ],
    derivatives: (state, params) => {
        const p = getState(state, 'p');
        const s = getParam(params, 's');
        const h = getParam(params, 'h');
        const q = 1 - p;
        const w_AA = 1 + s;
        const w_Aa = 1 + h * s;
        const w_aa = 1;
        const w_bar = p * p * w_AA + 2 * p * q * w_Aa + q * q * w_aa;
        const w_A = p * w_AA + q * w_Aa;
        const w_a = p * w_Aa + q * w_aa;
        const Delta_p = (p * q * (w_A - w_a)) / w_bar;
        return {
            p: Delta_p,
            t: 1,
        };
    },
    derivedState: (state, params) => {
        const p = getState(state, 'p');
        const t = getState(state, 't');
        const s = getParam(params, 's');
        const h = getParam(params, 'h');
        const q = 1 - p;
        const w_AA = 1 + s;
        const w_Aa = 1 + h * s;
        const w_aa = 1;
        const w_bar = p * p * w_AA + 2 * p * q * w_Aa + q * q * w_aa;
        return {
            q,
            w_AA,
            w_Aa,
            w_aa,
            w_bar,
            f_AA: p * p,
            f_Aa: 2 * p * q,
            f_aa: q * q,
            generation: t,
        };
    },
    failureModes: [
        {
            id: 'invalid-frequency',
            name: 'Invalid Allele Frequency',
            check: (_state, _params) => {
                const p = getState(getStateValues(_state), 'p');
                return p < 0 || p > 1;
            },
            severity: 'error',
            educationalMessage: 'Allele frequency must be between 0 and 1.',
        },
        {
            id: 'lethal-allele',
            name: 'Lethal Allele',
            check: (_state, params) => {
                const s = getParam(params, 's');
                return s <= -1;
            },
            severity: 'warning',
            educationalMessage: 'A selection coefficient s ≤ -1 indicates a lethal allele. The simulation may produce undefined behavior.',
        },
    ],
    bounds: {
        p: { min: 0, max: 1 },
        t: { min: 0, max: 1000 },
    },
});
// ============================================================================
// NATURAL SELECTION (STABILIZING)
// ============================================================================
export const NaturalSelectionStabilizing = createExperimentFromSchema({
    id: 'natural-selection-stabilizing',
    name: 'Natural Selection (Stabilizing)',
    domain: 'biology',
    description: 'Selection against extreme phenotypes (heterozygote advantage)',
    conceptTags: ['Stabilizing Selection', 'Heterozygote Advantage', 'Balanced Polymorphism', 'Overdominance'],
    difficulty: 6,
    stateVariables: [
        { id: 'p', name: 'Allele A Frequency', unit: '', initial: 0.5, symbol: 'p' },
        { id: 't', name: 'Generation', unit: 'generations', initial: 0, symbol: 't' },
    ],
    parameters: [
        { id: 's', name: 'Selection Against Homozygotes', unit: '', min: 0, max: 1, default: 0.2, symbol: 's', description: 'Fitness reduction for homozygotes' },
    ],
    equations: [
        {
            id: 'fitness-overdominance',
            name: 'Fitness with Heterozygote Advantage',
            latex: 'w_{AA} = 1 - s, \\quad w_{Aa} = 1, \\quad w_{aa} = 1 - s',
            description: 'Heterozygotes have highest fitness',
        },
        {
            id: 'equilibrium-frequency',
            name: 'Equilibrium Allele Frequency',
            latex: 'p^* = q^* = 0.5',
            description: 'Stable equilibrium at equal allele frequencies',
        },
    ],
    derivatives: (state, params) => {
        const p = getState(state, 'p');
        const s = getParam(params, 's');
        const q = 1 - p;
        const w_AA = 1 - s;
        const w_Aa = 1;
        const w_aa = 1 - s;
        const w_bar = p * p * w_AA + 2 * p * q * w_Aa + q * q * w_aa;
        const w_A = p * w_AA + q * w_Aa;
        const w_a = p * w_Aa + q * w_aa;
        const Delta_p = (p * q * (w_A - w_a)) / w_bar;
        return {
            p: Delta_p,
            t: 1,
        };
    },
    derivedState: (state, params) => {
        const p = getState(state, 'p');
        const t = getState(state, 't');
        const s = getParam(params, 's');
        const q = 1 - p;
        const w_AA = 1 - s;
        const w_Aa = 1;
        const w_aa = 1 - s;
        const w_bar = p * p * w_AA + 2 * p * q * w_Aa + q * q * w_aa;
        // At equilibrium, p = 0.5
        const distance_from_equilibrium = Math.abs(p - 0.5);
        return {
            q,
            w_AA,
            w_Aa,
            w_aa,
            w_bar,
            heterozygosity: 2 * p * q,
            distance_from_equilibrium,
            at_equilibrium: distance_from_equilibrium < 0.01 ? 1 : 0,
            generation: t,
        };
    },
    failureModes: [
        {
            id: 'invalid-frequency',
            name: 'Invalid Allele Frequency',
            check: (_state, _params) => {
                const p = getState(getStateValues(_state), 'p');
                return p < 0 || p > 1;
            },
            severity: 'error',
            educationalMessage: 'Allele frequency must be between 0 and 1.',
        },
        {
            id: 'negative-selection',
            name: 'Negative Selection Coefficient',
            check: (_state, params) => {
                const s = getParam(params, 's');
                return s < 0;
            },
            severity: 'warning',
            educationalMessage: 'A negative selection coefficient with stabilizing selection model would indicate disruptive selection instead.',
        },
    ],
    bounds: {
        p: { min: 0, max: 1 },
        t: { min: 0, max: 1000 },
    },
});
// ============================================================================
// MUTATION RATE EFFECTS
// ============================================================================
export const MutationRateEffects = createExperimentFromSchema({
    id: 'mutation-rate-effects',
    name: 'Mutation Rate Effects',
    domain: 'biology',
    description: 'Effect of mutation on allele frequencies in large populations',
    conceptTags: ['Mutation', 'Mutation-Selection Balance', 'Reverse Mutation', 'Genetic Load'],
    difficulty: 6,
    stateVariables: [
        { id: 'p', name: 'Allele A Frequency', unit: '', initial: 0.9, symbol: 'p', description: 'Frequency of allele A' },
        { id: 't', name: 'Generation', unit: 'generations', initial: 0, symbol: 't' },
    ],
    parameters: [
        { id: 'mu', name: 'Forward Mutation Rate', unit: 'per generation', min: 1e-8, max: 1e-3, default: 1e-5, symbol: '\\mu', description: 'A → a mutation rate per generation' },
        { id: 'nu', name: 'Reverse Mutation Rate', unit: 'per generation', min: 1e-8, max: 1e-3, default: 1e-6, symbol: '\\nu', description: 'a → A mutation rate per generation' },
        { id: 's', name: 'Selection Coefficient', unit: '', min: -1, max: 1, default: 0.01, symbol: 's', description: 'Selection against allele a (set to 0 for pure mutation)' },
    ],
    equations: [
        {
            id: 'mutation-balance',
            name: 'Mutation-Selection Balance',
            latex: 'p^* \\approx \\frac{\\nu}{\\mu + \\nu}',
            description: 'Equilibrium frequency with reversible mutation',
        },
        {
            id: 'mutation-selection-balance',
            name: 'Deleterious Mutation Balance',
            latex: '\\hat{q} \\approx \\sqrt{\\frac{\\mu}{s}}',
            description: 'Equilibrium frequency of deleterious allele under mutation-selection balance',
        },
        {
            id: 'recurrent-mutation',
            name: 'Change Due to Recurrent Mutation',
            latex: '\\Delta p = -\\mu p + \\nu q',
            description: 'Change in allele frequency from reversible mutation',
        },
    ],
    derivatives: (state, params) => {
        const p = getState(state, 'p');
        const mu = getParam(params, 'mu');
        const nu = getParam(params, 'nu');
        const s = getParam(params, 's');
        const q = 1 - p;
        // Mutation effect
        let Delta_p = -mu * p + nu * q;
        // Add selection if s > 0
        if (s > 0) {
            const w_AA = 1;
            const w_Aa = 1;
            const w_aa = 1 - s;
            const w_bar = p * p * w_AA + 2 * p * q * w_Aa + q * q * w_aa;
            const w_A = p * w_AA + q * w_Aa;
            const w_a = p * w_Aa + q * w_aa;
            const Delta_p_selection = (p * q * (w_A - w_a)) / w_bar;
            Delta_p += Delta_p_selection;
        }
        return {
            p: Delta_p,
            t: 1,
        };
    },
    derivedState: (state, params) => {
        const p = getState(state, 'p');
        const t = getState(state, 't');
        const mu = getParam(params, 'mu');
        const nu = getParam(params, 'nu');
        const s = getParam(params, 's');
        const q = 1 - p;
        const p_mutation_equilibrium = nu / (mu + nu);
        const q_mutation_selection = s > 0 ? Math.sqrt(mu / s) : 1;
        return {
            q,
            p_mutation_equilibrium,
            q_mutation_selection_equilibrium: q_mutation_selection,
            mutation_load: s * q,
            generation: t,
            mutation_rate_sum: mu + nu,
        };
    },
    failureModes: [
        {
            id: 'invalid-frequency',
            name: 'Invalid Allele Frequency',
            check: (_state, _params) => {
                const p = getState(getStateValues(_state), 'p');
                return p < 0 || p > 1;
            },
            severity: 'error',
            educationalMessage: 'Allele frequency must be between 0 and 1.',
        },
        {
            id: 'mutation-exceeds-selection',
            name: 'Mutation Rate Too High',
            check: (_state, params) => {
                const mu = getParam(params, 'mu');
                const s = getParam(params, 's');
                return s > 0 && mu > s;
            },
            severity: 'warning',
            educationalMessage: 'Forward mutation rate exceeds selection coefficient. Mutation will overwhelm selection, and the deleterious allele may reach high frequency.',
        },
    ],
    bounds: {
        p: { min: 0, max: 1 },
        t: { min: 0, max: 10000 },
    },
});
// ============================================================================
// GENE FLOW BETWEEN POPULATIONS
// ============================================================================
export const GeneFlowBetweenPopulations = createExperimentFromSchema({
    id: 'gene-flow-between-populations',
    name: 'Gene Flow Between Populations',
    domain: 'biology',
    description: 'Allele frequency changes due to migration between populations',
    conceptTags: ['Gene Flow', 'Migration', 'Population Structure', 'Isolation-by-Distance'],
    difficulty: 5,
    stateVariables: [
        { id: 'p1', name: 'Population 1 Allele Frequency', unit: '', initial: 0.9, symbol: 'p_1', description: 'Frequency in population 1' },
        { id: 'p2', name: 'Population 2 Allele Frequency', unit: '', initial: 0.1, symbol: 'p_2', description: 'Frequency in population 2' },
        { id: 't', name: 'Generation', unit: 'generations', initial: 0, symbol: 't' },
    ],
    parameters: [
        { id: 'm', name: 'Migration Rate', unit: 'per generation', min: 0, max: 0.5, default: 0.05, symbol: 'm', description: 'Fraction of population replaced by migrants each generation' },
    ],
    equations: [
        {
            id: 'gene-flow-pop1',
            name: 'Change in Population 1',
            latex: 'p_1\' = (1-m)p_1 + m p_2',
            description: 'New frequency after migration from pop 2 to pop 1',
        },
        {
            id: 'gene-flow-pop2',
            name: 'Change in Population 2',
            latex: 'p_2\' = (1-m)p_2 + m p_1',
            description: 'New frequency after migration from pop 1 to pop 2',
        },
        {
            id: 'island-model',
            name: 'Island Model Gene Flow',
            latex: '\\Delta p = m(p_{migrant} - p_{resident})',
            description: 'Change due to proportion of migrants',
        },
    ],
    derivatives: (state, params) => {
        const p1 = getState(state, 'p1');
        const p2 = getState(state, 'p2');
        const m = getParam(params, 'm');
        const p1_new = (1 - m) * p1 + m * p2;
        const p2_new = (1 - m) * p2 + m * p1;
        return {
            p1: p1_new - p1,
            p2: p2_new - p2,
            t: 1,
        };
    },
    derivedState: (state, params) => {
        const p1 = getState(state, 'p1');
        const p2 = getState(state, 'p2');
        const t = getState(state, 't');
        const m = getParam(params, 'm');
        const F_st = (p1 - p2) ** 2 / (4 * ((p1 + p2) / 2) * (1 - (p1 + p2) / 2));
        const p_mean = (p1 + p2) / 2;
        const divergence = Math.abs(p1 - p2);
        return {
            p_mean,
            divergence,
            F_st: isNaN(F_st) ? 0 : F_st,
            migration_effective: m,
            generations_to_homogenization: m > 0 ? Math.log(0.01) / Math.log(1 - 2 * m) : Infinity,
            generation: t,
        };
    },
    failureModes: [
        {
            id: 'invalid-frequency-p1',
            name: 'Invalid Population 1 Frequency',
            check: (_state, _params) => {
                const p1 = getState(getStateValues(_state), 'p1');
                return p1 < 0 || p1 > 1;
            },
            severity: 'error',
            educationalMessage: 'Allele frequency must be between 0 and 1.',
        },
        {
            id: 'invalid-frequency-p2',
            name: 'Invalid Population 2 Frequency',
            check: (_state, _params) => {
                const p2 = getState(getStateValues(_state), 'p2');
                return p2 < 0 || p2 > 1;
            },
            severity: 'error',
            educationalMessage: 'Allele frequency must be between 0 and 1.',
        },
        {
            id: 'excessive-migration',
            name: 'Excessive Migration Rate',
            check: (_state, params) => {
                const m = getParam(params, 'm');
                return m > 0.25;
            },
            severity: 'warning',
            educationalMessage: 'Migration rate > 25% per generation is very high. Populations will homogenize rapidly.',
        },
    ],
    bounds: {
        p1: { min: 0, max: 1 },
        p2: { min: 0, max: 1 },
        t: { min: 0, max: 500 },
    },
});
//# sourceMappingURL=genetics.js.map